import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { setHankoLang } from '@/auth/hanko';
import { format, type Locale, type MessageKey } from './messages';

interface LocaleContextValue {
  locale: Locale;
  setLocale: (next: Locale) => void;
}

const LocaleContext = createContext<LocaleContextValue | null>(null);

// Deutsch ist der Default (BILLING_PLAN.md §13); en ist vorbereitet, aber P3.3 laut Plan
// (Sprachschalter in der UI kommt später — für die Baseline reicht ein fest verdrahtetes 'de').
const DEFAULT_LOCALE: Locale = 'de';

export function LocaleProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(DEFAULT_LOCALE);

  const setLocale = useCallback((next: Locale) => {
    setLocaleState(next);
  }, []);

  useEffect(() => {
    document.documentElement.lang = locale;
    setHankoLang(locale);
  }, [locale]);

  const value = useMemo(() => ({ locale, setLocale }), [locale, setLocale]);

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export function useLocale(): LocaleContextValue {
  const ctx = useContext(LocaleContext);
  if (!ctx) throw new Error('useLocale must be used inside <LocaleProvider>');
  return ctx;
}

export function useT() {
  const { locale } = useLocale();
  return useCallback(
    (key: MessageKey, replacements?: Record<string, string | number>) => format(key, locale, replacements),
    [locale],
  );
}

export function useLocaleAwareFormatters() {
  const { locale } = useLocale();
  return useMemo(() => {
    const tag = locale === 'de' ? 'de-DE' : 'en-US';
    const date = (input: string | Date) =>
      new Intl.DateTimeFormat(tag, { year: 'numeric', month: 'short', day: 'numeric' }).format(new Date(input));
    const currency = (cents: number, code = 'EUR') =>
      new Intl.NumberFormat(tag, { style: 'currency', currency: code }).format(cents / 100);
    return { date, currency };
  }, [locale]);
}
